import {
	BreakpointSummaryPayload,
	CurrencyType,
	SecurityValueOverrideComponent,
	SecurityValueOverrideField,
} from '@dealstack/shared/lib/generated';
import { BaseTransformer } from '@dealstack/frontend/app/pages/Valuations/components/table/BaseTransformer';
import { BaseValuationRow } from '@dealstack/frontend/app/pages/Valuations/components/table/types';
import { flatMapDeep } from 'lodash';
import { useTranslationValuations } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { DateTime } from 'luxon';
import { formatDecimal, formatNumber } from '@dealstack/shared/lib/format/number';
import { HighPrecisionDecimal } from '@dealstack/shared/lib/format/currency/highPrecisionDecimal';
import {
	formatPerUnitMoney,
	formatSummaryDecimal,
	formatSummaryDecimalString,
} from '@dealstack/frontend/app/pages/Valuations/components/table/utils/formatting';

export enum BreakpointSummaryColumnKey {
	SECURITY = 'security',
	FROM = 'from',
	TO = 'to',
	PROCEEDS_IN_TIER = 'proceedsInTier',
	REMAINING_TO_DISTRIBUTE = 'remainingToDistribute',
	PARTICIPATING_SECURITIES = 'participatingSecurities',
	PROCEEDS_PER_SECURITY = 'proceedsPerSecurity',
	ACTIONS = 'actions',
}

export enum BreakpointSummaryRowType {
	HEADER = 'header',
	PERSON_INPUT = 'personInput',
	TIER = 'tier',
	BREAKPOINT_SECURITY = 'breakpointSecurity',
	RESIDUE_TIER = 'residueTier',
}

export interface BreakpointSummaryRow extends BaseValuationRow {
	subRows?: BreakpointSummaryRow[];
	metadata: BaseValuationRow['metadata'] & {
		securityId?: string;
		overrideId?: string;
		overrideField?: SecurityValueOverrideField;
		securityComponent?: SecurityValueOverrideComponent;
		trancheKey?: string;
		type?: BreakpointSummaryRowType;
	};
}

export class BreakpointSummaryTransformer extends BaseTransformer<
	BreakpointSummaryPayload,
	BreakpointSummaryRow
> {
	constructor(
		private readonly tValuations: ReturnType<typeof useTranslationValuations>,
		private readonly currency: CurrencyType
	) {
		super();
	}

	static createRow(params: {
		// Row data
		id: string;
		rowType: BreakpointSummaryRowType;
		depth?: number;
		security: string;
		from?: string | number;
		to?: string | number;
		proceedsInTier?: string | number;
		remainingToDistribute?: string | number;
		participatingSecurities?: string | number;
		proceedsPerSecurity?: string | number;
		trancheKey?: string;
		// Metadata fields
		hasToggle?: boolean;
		isToggled?: boolean;
		securityId?: string;
		overrideId?: string;
		overrideField?: SecurityValueOverrideField;
		securityComponent?: SecurityValueOverrideComponent;
		// Subrows
		subRows?: BreakpointSummaryRow[];
		numberFormatter?: (value: number) => string;
	}): BreakpointSummaryRow {
		const {
			id,
			rowType,
			depth = 0,
			security,
			from = '',
			to = '',
			proceedsInTier = '',
			remainingToDistribute = '',
			participatingSecurities = '',
			proceedsPerSecurity = '',
			hasToggle = false,
			isToggled = false,
			securityId,
			trancheKey,
			overrideId,
			overrideField,
			securityComponent,
			subRows,
		} = params;

		const formattedFrom = typeof from === 'number' ? this.formatNumber(from) : from;
		const formattedTo = typeof to === 'number' ? this.formatNumber(to) : to;
		const formattedProceedsInTier =
			typeof proceedsInTier === 'number' ? this.formatNumber(proceedsInTier) : proceedsInTier;
		const formattedRemainingToDistribute =
			typeof remainingToDistribute === 'number'
				? this.formatNumber(remainingToDistribute)
				: remainingToDistribute;
		const formattedParticipatingSecurities =
			typeof participatingSecurities === 'number'
				? formatNumber({ value: participatingSecurities, minPrecision: 0, maxPrecision: 6 })
				: participatingSecurities;
		const formattedProceedsPerSecurity =
			typeof proceedsPerSecurity === 'number'
				? this.formatNumber(proceedsPerSecurity)
				: proceedsPerSecurity;

		return {
			id,
			values: {
				[BreakpointSummaryColumnKey.SECURITY]: security,
				[BreakpointSummaryColumnKey.FROM]: formattedFrom,
				[BreakpointSummaryColumnKey.TO]: formattedTo,
				[BreakpointSummaryColumnKey.PROCEEDS_IN_TIER]: formattedProceedsInTier,
				[BreakpointSummaryColumnKey.REMAINING_TO_DISTRIBUTE]:
					formattedRemainingToDistribute,
				[BreakpointSummaryColumnKey.PARTICIPATING_SECURITIES]:
					formattedParticipatingSecurities,
				[BreakpointSummaryColumnKey.PROCEEDS_PER_SECURITY]: formattedProceedsPerSecurity,
			},
			metadata: {
				type: rowType,
				depth,
				...(hasToggle && { hasToggle }),
				...(isToggled && { isToggled }),
				...(securityId && { securityId }),
				...(overrideId && { overrideId }),
				...(overrideField && { overrideField }),
				...(securityComponent && { securityComponent }),
				...(trancheKey && { trancheKey }),
			},
			...(subRows && { subRows }),
		};
	}

	// Tiers are named after the issuer's security ranks (WAT-651); the seniority-index wording is
	// the fallback for tiers with no matching rank definition.
	private generateTierHeader(
		tier: BreakpointSummaryPayload['tiers'][number],
		tierCount: number
	): string {
		if (tier.rankName) {
			return this.tValuations('breakpoint_summary.named_tier_header', {
				tierCount,
				rankName: tier.rankName,
				defaultValue: '{{tierCount}}. {{rankName}}',
			});
		}
		return tier.seniorityIndex < Number.MAX_SAFE_INTEGER
			? this.tValuations('breakpoint_summary.normal_tier_header', {
					tierCount,
					seniorityIndex: tier.seniorityIndex,
				})
			: this.tValuations('breakpoint_summary.max_tier_header', {
					tierCount,
				});
	}

	toTableData(summaryData?: BreakpointSummaryPayload): BreakpointSummaryRow[] {
		if (!summaryData) {
			return [];
		}

		const rows: BreakpointSummaryRow[] = [];
		let toValue = new HighPrecisionDecimal(0);
		let fromValue = new HighPrecisionDecimal(0);
		let remainingToDistribute = new HighPrecisionDecimal(summaryData.startingBalance);

		// Starting Balance
		rows.push(
			BreakpointSummaryTransformer.createRow({
				id: 'starting-balance',
				rowType: BreakpointSummaryRowType.HEADER,
				security: this.tValuations('breakpoint_summary.starting_balance_numbered_header', {
					rowNumber: rows.length + 1,
				}),
				from: '-',
				to: '-',
				proceedsInTier: '-',
				remainingToDistribute: formatSummaryDecimal(remainingToDistribute),
				participatingSecurities: this.tValuations(
					'breakpoint_summary.not_applicable_value'
				),
				proceedsPerSecurity: this.tValuations('breakpoint_summary.not_applicable_value'),
				depth: 0,
			})
		);

		// WAT-744 warrant-holder decomposition. The starting balance is the holder's GROSS diluted shares.
		// A single netted row shows the strike it paid to exercise LESS the strike it recovered through the
		// sub-pour (its residual share of the recirculated strike); the running balance drops by that net so
		// the retained row below lands on what it actually keeps. Renders only when the node paid a strike;
		// every other node keeps the original "retained cash first" flow untouched.
		const warrantStrikePaid = new HighPrecisionDecimal(
			summaryData.warrantsPayoutProceeds ?? '0'
		);
		const warrantStrikeRecovered = new HighPrecisionDecimal(
			summaryData.warrantStrikeRecovered ?? '0'
		);
		if (warrantStrikePaid.gt(0)) {
			const warrantStrikeNet = warrantStrikePaid.sub(warrantStrikeRecovered);
			fromValue = toValue;
			toValue = toValue.add(warrantStrikeNet);
			remainingToDistribute = new HighPrecisionDecimal(summaryData.startingBalance).sub(
				toValue
			);
			rows.push(
				BreakpointSummaryTransformer.createRow({
					id: 'warrant-exercise-price',
					rowType: BreakpointSummaryRowType.PERSON_INPUT,
					security: this.tValuations(
						'breakpoint_summary.warrant_exercise_price_net_header',
						{
							rowNumber: rows.length + 1,
							defaultValue: '{{rowNumber}}. Net exercise price',
						}
					),
					from: formatSummaryDecimal(fromValue),
					to: formatSummaryDecimal(toValue),
					proceedsInTier: formatSummaryDecimal(warrantStrikeNet),
					remainingToDistribute: formatSummaryDecimal(remainingToDistribute),
					participatingSecurities: this.tValuations(
						'breakpoint_summary.not_applicable_value'
					),
					proceedsPerSecurity: this.tValuations(
						'breakpoint_summary.not_applicable_value'
					),
					depth: 0,
				})
			);
		}

		// Retained cash and holdbacks. Additive (toValue += retained) so it follows any decomposition rows
		// above; for a node with no warrant strike this is the first consuming row, identical to before.
		fromValue = toValue;
		toValue = toValue.add(new HighPrecisionDecimal(summaryData.retainedCash));
		remainingToDistribute = new HighPrecisionDecimal(summaryData.startingBalance).sub(toValue);
		rows.push(
			BreakpointSummaryTransformer.createRow({
				id: 'retained-cash',
				rowType: BreakpointSummaryRowType.PERSON_INPUT,
				security: this.tValuations('breakpoint_summary.retained_cash_numbered_header', {
					rowNumber: rows.length + 1,
				}),
				from: warrantStrikePaid.gt(0) ? formatSummaryDecimal(fromValue) : '-',
				to: warrantStrikePaid.gt(0)
					? formatSummaryDecimal(toValue)
					: formatSummaryDecimalString(summaryData.retainedCash ?? '0'),
				proceedsInTier: formatSummaryDecimalString(summaryData.retainedCash ?? '0'),
				remainingToDistribute: formatSummaryDecimal(remainingToDistribute),
				participatingSecurities: this.tValuations(
					'breakpoint_summary.not_applicable_value'
				),
				proceedsPerSecurity: this.tValuations('breakpoint_summary.not_applicable_value'),
				depth: 0,
			})
		);

		// Payables
		fromValue = toValue;
		toValue = toValue.add(new HighPrecisionDecimal(summaryData.outgoingPayables));
		remainingToDistribute = new HighPrecisionDecimal(summaryData.startingBalance).sub(toValue);
		rows.push(
			BreakpointSummaryTransformer.createRow({
				id: 'outgoing-payables',
				rowType: BreakpointSummaryRowType.PERSON_INPUT,
				security: this.tValuations('breakpoint_summary.payables_numbered_header', {
					rowNumber: rows.length + 1,
				}),
				from: formatSummaryDecimal(fromValue),
				to: formatSummaryDecimal(toValue),
				proceedsInTier: formatSummaryDecimalString(summaryData.outgoingPayables ?? '0'),
				remainingToDistribute: formatSummaryDecimal(remainingToDistribute),
				participatingSecurities: this.tValuations(
					'breakpoint_summary.not_applicable_value'
				),
				proceedsPerSecurity: this.tValuations('breakpoint_summary.not_applicable_value'),
				depth: 0,
			})
		);

		// Tiers (from most senior to least)
		const componentToNameMap = {
			[SecurityValueOverrideComponent.COMPONENT_INTEREST]: this.tValuations(
				'breakpoint_summary.interest_component_name'
			),
			[SecurityValueOverrideComponent.COMPONENT_PRINCIPAL]: this.tValuations(
				'breakpoint_summary.principal_component_name'
			),
			[SecurityValueOverrideComponent.COMPONENT_LIQUIDATION_PREFERENCE]: this.tValuations(
				'breakpoint_summary.liquidation_preference_component_name',
				{ defaultValue: 'Liquidation preference' }
			),
			[SecurityValueOverrideComponent.COMPONENT_RESIDUE]: this.tValuations(
				'breakpoint_summary.residue_component_name'
			),
			[SecurityValueOverrideComponent.COMPONENT_CATCH_UP]: this.tValuations(
				'breakpoint_summary.catch_up_component_name'
			),
			[SecurityValueOverrideComponent.COMPONENT_TRACKING]: this.tValuations(
				'breakpoint_summary.tracking_component_name'
			),
			[SecurityValueOverrideComponent.WHOLE_SECURITY]: '',
		} as const satisfies Record<SecurityValueOverrideComponent, string>;

		// First tier number follows the flow rows pushed above (starting balance, optional WAT-744 net
		// exercise price, retained cash, payables) — self-adjusts so the extra use row shifts numbering.
		let tierCount = rows.length + 1;
		for (const tier of summaryData.tiers) {
			if (tier.securities?.length > 0) {
				const proceedsInTier = new HighPrecisionDecimal(tier.totalProceeds);

				fromValue = toValue;
				toValue = toValue.add(proceedsInTier);
				remainingToDistribute = new HighPrecisionDecimal(summaryData.startingBalance).sub(
					toValue
				);
				const participatingSecurities = new HighPrecisionDecimal(tier.totalUnits);

				const tierRow = BreakpointSummaryTransformer.createRow({
					id: `tier-${tier.seniorityIndex}`,
					rowType: BreakpointSummaryRowType.TIER,
					security: this.generateTierHeader(tier, tierCount),
					from: formatSummaryDecimal(fromValue),
					to: formatSummaryDecimal(toValue),
					proceedsInTier: formatSummaryDecimal(proceedsInTier),
					remainingToDistribute: formatSummaryDecimal(remainingToDistribute),
					participatingSecurities: formatDecimal({
						value: participatingSecurities,
						maxPrecision: 5,
					}),
					proceedsPerSecurity: this.tValuations(
						'breakpoint_summary.not_applicable_value'
					),
					depth: 0,
					hasToggle: true,
					isToggled: false,
					subRows: [],
				});

				if (tier.securities.length) {
					tierRow.subRows = tier.securities.map((security) => {
						let proceedsInTier = new HighPrecisionDecimal(0);
						let proceedsPerSecurity = new HighPrecisionDecimal(0);
						if (!security.override) {
							proceedsInTier = new HighPrecisionDecimal(security.totalProceeds);
							proceedsPerSecurity =
								Number(security.totalUnits) > 0
									? new HighPrecisionDecimal(security.totalProceeds).div(
											security.totalUnits
										)
									: new HighPrecisionDecimal(0);
						} else {
							switch (security.override.overrideField) {
								case SecurityValueOverrideField.VALUE_TOTAL_ISSUED:
									proceedsInTier = new HighPrecisionDecimal(
										security.override.overrideValue
									);
									proceedsPerSecurity = proceedsInTier.div(security.totalUnits);
									break;
								case SecurityValueOverrideField.VALUE_PER_UNIT:
									proceedsPerSecurity = new HighPrecisionDecimal(
										security.override.overrideValue
									);
									proceedsInTier = proceedsPerSecurity.times(security.totalUnits);

									break;
								default:
									proceedsPerSecurity = new HighPrecisionDecimal(0);
									break;
							}
						}

						const generateSecurityName = () => {
							const issuedOn = security.trancheKey
								? ` (${this.tValuations('breakpoint_summary.issue_on', {
										issueDate: DateTime.fromISO(security.trancheKey, {
											zone: 'utc',
										}).toFormat('dd/MM/yyyy'),
									})})`
								: '';

							const componentName =
								security.component !== SecurityValueOverrideComponent.WHOLE_SECURITY
									? ` ${componentToNameMap[security.component]}` // Only show the component name if it's not the whole security
									: '';

							return `${security.securityName}${issuedOn}${componentName}`;
						};

						return BreakpointSummaryTransformer.createRow({
							id: `${tierRow.id}-${security.securityId}`,
							rowType: BreakpointSummaryRowType.BREAKPOINT_SECURITY,
							security: generateSecurityName(),
							from: '',
							to: '',
							proceedsInTier: formatSummaryDecimal(proceedsInTier),
							remainingToDistribute: '',
							participatingSecurities: formatDecimal({
								value: new HighPrecisionDecimal(security.totalUnits ?? '0'),
								maxPrecision: 5,
							}),
							proceedsPerSecurity: formatPerUnitMoney(proceedsPerSecurity),
							depth: 1,
							securityId: security.securityId,
							overrideId: security.override?.id,
							overrideField: security.override?.overrideField,
							securityComponent: security.component,
							trancheKey: security.trancheKey,
						});
					});
				}
				rows.push(tierRow);
			}
			tierCount += 1;
		}

		// Residue Tier
		if (summaryData.residueTier && summaryData.residueTier.securities.length > 0) {
			const participatingSecurities = new HighPrecisionDecimal(
				summaryData.residueTier.totalUnits
			);

			fromValue = toValue;
			toValue = new HighPrecisionDecimal(summaryData.residueTier.totalProceeds).add(
				fromValue
			);
			remainingToDistribute = new HighPrecisionDecimal(summaryData.startingBalance).sub(
				toValue
			);

			const residueTierRow = BreakpointSummaryTransformer.createRow({
				id: 'residue-tier',
				rowType: BreakpointSummaryRowType.RESIDUE_TIER,
				security: this.tValuations('breakpoint_summary.residue_tier_header', {
					tierCount,
				}),
				from: formatSummaryDecimal(fromValue),
				to: formatSummaryDecimal(toValue),
				proceedsInTier: formatSummaryDecimalString(
					summaryData.residueTier.totalProceeds ?? '0'
				),
				remainingToDistribute: formatSummaryDecimal(remainingToDistribute),
				participatingSecurities: formatDecimal({
					value: participatingSecurities,
					maxPrecision: 5,
				}),
				proceedsPerSecurity: this.tValuations('breakpoint_summary.not_applicable_value'),
				depth: 0,
				hasToggle: summaryData.residueTier.securities.length > 0,
				isToggled: false,
				subRows: [],
			});

			residueTierRow.subRows = summaryData.residueTier.securities.map((security) => {
				let proceedsInTier = new HighPrecisionDecimal(0);
				let proceedsPerSecurity = new HighPrecisionDecimal(0);
				proceedsInTier = new HighPrecisionDecimal(security.totalProceeds);
				proceedsPerSecurity =
					Number(security.totalUnits) > 0
						? new HighPrecisionDecimal(security.totalProceeds).div(security.totalUnits)
						: new HighPrecisionDecimal(0);

				return BreakpointSummaryTransformer.createRow({
					id: `${residueTierRow.id}-${security.securityId}`,
					rowType: BreakpointSummaryRowType.BREAKPOINT_SECURITY,
					security: security.securityName,
					from: '',
					to: '',
					proceedsInTier: formatSummaryDecimal(proceedsInTier),
					remainingToDistribute: '',
					participatingSecurities: formatDecimal({
						value: new HighPrecisionDecimal(security.totalUnits ?? '0'),
						maxPrecision: 5,
					}),
					proceedsPerSecurity: formatPerUnitMoney(proceedsPerSecurity),
					depth: 1,
					securityId: security.securityId,
				});
			});

			rows.push(residueTierRow);
		}

		const result = flatMapDeep(rows, (row) => [row, ...(row.subRows ?? [])]);

		return result;
	}
}
